<template>
    <div>
        <app-template>
            <div class="px-4 pt-8 pb-28">

                <!-- Header -->
                <div class="mb-8">
                    <h1 class="text-2xl font-semibold text-gray-900 tracking-tight">Paramètres de facturation</h1>
                    <p class="text-sm text-gray-400 mt-1">Gérez votre catalogue et vos préférences de paiement.</p>
                </div>

                <!-- Tabs -->
                <div class="flex gap-1 bg-gray-100 rounded-xl p-1 mb-8 w-fit">
                    <button
                        @click="tab = 'catalogue'"
                        class="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                        :class="tab === 'catalogue' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                    >
                        <i class="fas fa-list mr-2"></i>Catalogue
                        <i v-if="!wc.settings_facturation_catalogue" class="fas fa-exclamation-triangle text-orange-500 fa-beat-fade ml-1"></i>
                    </button>
                    <button
                        @click="tab = 'paiement'"
                        class="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                        :class="tab === 'paiement' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                    >
                        <i class="fas fa-file-invoice-dollar mr-2"></i>Paiement & mentions
                        <i v-if="!wc.settings_facturation_paiement" class="fas fa-exclamation-triangle text-orange-500 fa-beat-fade ml-1"></i>
                    </button>
                </div>

                <!-- Tab : Catalogue -->
                <div v-if="tab === 'catalogue'">
                    <parametre-facturation-catalogue />
                </div>

                <!-- Tab : Paiement & mentions -->
                <div v-if="tab === 'paiement'">

                    <!-- Skeleton loading -->
                    <div v-if="loading" class="space-y-4">
                        <div class="bg-white rounded-2xl border border-gray-100 p-6">
                            <div class="h-4 bg-gray-100 rounded w-1/3 mb-4 animate-pulse"></div>
                            <div class="space-y-3">
                                <div class="h-10 bg-gray-100 rounded-lg animate-pulse"></div>
                                <div class="h-10 bg-gray-100 rounded-lg animate-pulse"></div>
                            </div>
                        </div>
                    </div>

                    <div v-else class="space-y-4">

                        <!-- Bloc : Coordonnées bancaires -->
                        <div class="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div class="px-6 pt-5 pb-2 flex items-center gap-3 border-b border-gray-50">
                                <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                                    <i class="fas fa-university text-orange-500 text-sm"></i>
                                </div>
                                <div>
                                    <h2 class="text-sm font-semibold text-gray-800">Coordonnées bancaires</h2>
                                    <p class="text-xs text-gray-400">Affichées sur vos factures si aucun IBAN n'est renseigné sur le document.</p>
                                </div>
                            </div>

                            <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">

                                <!-- IBAN -->
                                <div class="md:col-span-2">
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                        IBAN <span class="text-orange-500">*</span>
                                    </label>
                                    <input
                                        v-model="form.iban"
                                        type="text"
                                        placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                                        :class="[
                                            'w-full rounded-xl border px-4 py-2.5 text-sm font-mono transition-all outline-none',
                                            formErrors.iban
                                                ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200 focus:border-red-400'
                                                : 'border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400'
                                        ]"
                                    />
                                    <p v-if="formErrors.iban" class="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                                        <i class="fas fa-circle-exclamation"></i>{{ formErrors.iban }}
                                    </p>
                                </div>

                                <!-- BIC -->
                                <div>
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                        BIC / SWIFT <span class="text-orange-500">*</span>
                                    </label>
                                    <input
                                        v-model="form.bic"
                                        type="text"
                                        placeholder="BNPAFRPPXXX"
                                        :class="[
                                            'w-full rounded-xl border px-4 py-2.5 text-sm font-mono transition-all outline-none',
                                            formErrors.bic
                                                ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200 focus:border-red-400'
                                                : 'border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400'
                                        ]"
                                    />
                                    <p v-if="formErrors.bic" class="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                                        <i class="fas fa-circle-exclamation"></i>{{ formErrors.bic }}
                                    </p>
                                </div>

                                <!-- Titulaire -->
                                <div>
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                        Titulaire du compte <span class="text-orange-500">*</span>
                                    </label>
                                    <input
                                        v-model="form.iban_titulaire"
                                        type="text"
                                        placeholder="Ex : SAS Dupont Chauffage"
                                        :class="[
                                            'w-full rounded-xl border px-4 py-2.5 text-sm transition-all outline-none',
                                            formErrors.iban_titulaire
                                                ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200 focus:border-red-400'
                                                : 'border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400'
                                        ]"
                                    />
                                    <p v-if="formErrors.iban_titulaire" class="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                                        <i class="fas fa-circle-exclamation"></i>{{ formErrors.iban_titulaire }}
                                    </p>
                                </div>

                            </div>
                        </div>

                        <!-- Bloc : Conditions de paiement -->
                        <div class="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div class="px-6 pt-5 pb-2 flex items-center gap-3 border-b border-gray-50">
                                <div class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                    <i class="fas fa-clock text-blue-500 text-sm"></i>
                                </div>
                                <div>
                                    <h2 class="text-sm font-semibold text-gray-800">Conditions de paiement</h2>
                                    <p class="text-xs text-gray-400">Ces informations apparaissent dans le bloc règlement de vos factures.</p>
                                </div>
                            </div>

                            <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div :class="form.conditions_paiement === 'custom' ? '' : 'md:col-span-2'">
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Délai de paiement</label>
                                    <select
                                        v-model="form.conditions_paiement"
                                        class="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none transition-all"
                                    >
                                        <option value="À réception de facture">À réception de facture</option>
                                        <option value="Comptant">Comptant</option>
                                        <option value="15 jours net">15 jours net</option>
                                        <option value="30 jours net">30 jours net</option>
                                        <option value="45 jours net">45 jours net</option>
                                        <option value="60 jours net">60 jours net</option>
                                        <option value="custom">Personnalisé…</option>
                                    </select>
                                </div>

                                <div v-if="form.conditions_paiement === 'custom'">
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                        Condition personnalisée <span class="text-orange-500">*</span>
                                    </label>
                                    <input
                                        v-model="form.conditions_paiement_custom"
                                        type="text"
                                        placeholder="Ex : 30 jours fin de mois"
                                        :class="[
                                            'w-full rounded-xl border px-4 py-2.5 text-sm transition-all outline-none',
                                            formErrors.conditions_paiement_custom
                                                ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200 focus:border-red-400'
                                                : 'border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400'
                                        ]"
                                    />
                                    <p v-if="formErrors.conditions_paiement_custom" class="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                                        <i class="fas fa-circle-exclamation"></i>{{ formErrors.conditions_paiement_custom }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Bloc : Mentions légales -->
                        <div class="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                            <div class="px-6 pt-5 pb-2 flex items-center gap-3 border-b border-gray-50">
                                <div class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                                    <i class="fas fa-scale-balanced text-purple-500 text-sm"></i>
                                </div>
                                <div>
                                    <h2 class="text-sm font-semibold text-gray-800">Mentions légales</h2>
                                    <p class="text-xs text-gray-400">Apparaissent en bas de vos factures. Le minimum légal est imposé pour les factures B2B.</p>
                                </div>
                            </div>

                            <div class="p-6 space-y-4">

                                <!-- Taux de pénalités -->
                                <div>
                                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                        Taux de pénalités de retard <span class="text-orange-500">*</span>
                                        <span class="text-gray-400 font-normal normal-case ml-1">— minimum légal : 3× le taux d'intérêt légal</span>
                                    </label>
                                    <input
                                        v-model="form.taux_penalites"
                                        type="text"
                                        placeholder="3 fois le taux d'intérêt légal en vigueur"
                                        :class="[
                                            'w-full rounded-xl border px-4 py-2.5 text-sm transition-all outline-none',
                                            formErrors.taux_penalites
                                                ? 'border-red-300 bg-red-50 focus:ring-2 focus:ring-red-200 focus:border-red-400'
                                                : 'border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400'
                                        ]"
                                    />
                                    <p v-if="formErrors.taux_penalites" class="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                                        <i class="fas fa-circle-exclamation"></i>{{ formErrors.taux_penalites }}
                                    </p>
                                </div>

                                <!-- Indemnité forfaitaire — lecture seule -->
                                <div class="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                                    <i class="fas fa-lock text-amber-400 mt-0.5 flex-shrink-0 text-sm"></i>
                                    <div>
                                        <p class="text-sm font-medium text-amber-800">Indemnité forfaitaire de recouvrement : 40 €</p>
                                        <p class="text-xs text-amber-600 mt-0.5">Montant fixé par la loi (art. L.441-10 C.com.) — non modifiable, appliqué automatiquement sur les factures B2B.</p>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <!-- Aperçu mention légale -->
                        <div class="bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-5">
                            <div class="flex items-center gap-2 mb-3">
                                <i class="fas fa-eye text-gray-400 text-xs"></i>
                                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Aperçu — mention en bas de facture B2B</p>
                            </div>
                            <p class="text-xs text-gray-500 leading-relaxed italic">
                                En cas de retard de paiement, des pénalités de retard calculées au taux de
                                <strong class="text-gray-700">{{ conditionsPaiementLabel }}</strong>
                                seront appliquées de plein droit, ainsi qu'une indemnité forfaitaire de
                                <strong class="text-gray-700">40 €</strong> pour frais de recouvrement (Art. L.441-10 du Code de commerce).
                                Aucun escompte consenti pour paiement anticipé.
                            </p>
                        </div>

                    </div>

                    <!-- Bouton save flottant -->
                    <div v-if="!loading" class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2">

                        <transition name="fade-up">
                            <div
                                v-if="showValidationBanner && !isFormValid"
                                class="flex items-center gap-2 bg-red-600 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg"
                            >
                                <i class="fas fa-triangle-exclamation"></i>
                                Complétez les champs obligatoires avant d'enregistrer.
                            </div>
                        </transition>

                        <button
                            @click="handleSave"
                            :disabled="saving"
                            :class="[
                                'flex items-center gap-2 font-medium px-8 py-3.5 rounded-full transition-all duration-300 shadow-2xl',
                                isFormValid
                                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white hover:scale-105 hover:shadow-orange-200'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                                saving ? 'opacity-60' : ''
                            ]"
                        >
                            <i v-if="!saving" class="fas fa-save text-sm"></i>
                            <svg v-else class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
                        </button>

                    </div>
                </div>

            </div>
        </app-template>
    </div>
</template>

<style scoped>
.fade-up-enter-active,
.fade-up-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>

<script>
import ParametreFacturationCatalogue from './ParametreFacturationCatalogue.vue';

export default {
    name: 'ParametreFacturation',

    components: {
        ParametreFacturationCatalogue,
    },

    data() {
        return {
            tab:                  'catalogue',
            loading:              false,
            saving:               false,
            showValidationBanner: false,

            wcOverwrite: null,

            form: {
                iban:                       '',
                bic:                        '',
                iban_titulaire:             '',
                conditions_paiement:        '30 jours net',
                conditions_paiement_custom: '',
                taux_penalites:             '3 fois le taux d\'intérêt légal en vigueur',
            },
        };
    },

    computed: {
        formErrors() {
            const errors = {};

            if (!this.form.iban.trim())
                errors.iban = 'L\'IBAN est obligatoire.';

            if (!this.form.bic.trim())
                errors.bic = 'Le BIC / SWIFT est obligatoire.';

            if (!this.form.iban_titulaire.trim())
                errors.iban_titulaire = 'Le titulaire du compte est obligatoire.';

            if (!this.form.taux_penalites.trim())
                errors.taux_penalites = 'Le taux de pénalités de retard est obligatoire.';

            if (this.form.conditions_paiement === 'custom' && !this.form.conditions_paiement_custom.trim())
                errors.conditions_paiement_custom = 'Veuillez saisir une condition personnalisée.';

            return errors;
        },

        isFormValid() {
            return Object.keys(this.formErrors).length === 0;
        },

        conditionsPaiementLabel() {
            return this.form.taux_penalites || '3 fois le taux d\'intérêt légal en vigueur';
        },

        conditionsPaiementValue() {
            return this.form.conditions_paiement === 'custom'
                ? this.form.conditions_paiement_custom
                : this.form.conditions_paiement;
        },

        wc() {
            if (this.wcOverwrite) {
                return this.wcOverwrite
            }
            return this.$account.account.societe.config_warnings
        }
    },

    watch: {
        tab(val) {
            if (val === 'paiement' && !this.form.iban) {
                this.load();
            }
        },
    },

    methods: {
        async load() {
            this.loading = true;
            try {
                const res = await this.$request.get('/societe/get');

                this.form.iban           = res.iban           ?? '';
                this.form.bic            = res.bic            ?? '';
                this.form.iban_titulaire = res.iban_titulaire ?? '';
                this.form.taux_penalites = res.taux_penalites || '3 fois le taux d\'intérêt légal en vigueur';

                const knownDelais = [
                    'À réception de facture', 'Comptant',
                    '15 jours net', '30 jours net', '45 jours net', '60 jours net',
                ];
                const cp = res.conditions_paiement ?? '30 jours net';
                if (knownDelais.includes(cp)) {
                    this.form.conditions_paiement = cp;
                } else {
                    this.form.conditions_paiement        = 'custom';
                    this.form.conditions_paiement_custom = cp;
                }
            } finally {
                this.loading = false;
            }
        },

        handleSave() {
            if (!this.isFormValid) {
                this.showValidationBanner = true;
                setTimeout(() => { this.showValidationBanner = false; }, 3500);
                return;
            }
            this.save();
        },

        async save() {
            this.saving = true;
            try {
                await this.$request.post('/societe/update-paiement', {
                    iban:                this.form.iban,
                    bic:                 this.form.bic,
                    iban_titulaire:      this.form.iban_titulaire,
                    conditions_paiement: this.conditionsPaiementValue,
                    taux_penalites:      this.form.taux_penalites,
                });
                this.$toast?.success('Paramètres enregistrés');
                this.$eventHub.$emit('refresh-warnings');
            } catch {
                this.$toast?.error('Erreur lors de la sauvegarde.');
            } finally {
                this.saving = false;
            }
        },

        async getWarnings() {
            try {
                const response = await this.$request.get('/config-warnings');
                this.wcOverwrite = response;
            } catch (error) {
                console.error('Erreur lors de la récupération des warnings de configuration', error);
            }
        },
    },

    mounted() {
        this.$eventHub.$on('refresh-warnings', this.getWarnings);
    },
};
</script>